/**
 * ColumnBlock — Side-by-side column layout (2 or 3 columns).
 * Each column is a NestedEditor sub-editor instance.
 */

import { useCallback, useMemo, lazy, Suspense } from 'react';
import { createReactBlockSpec } from '@blocknote/react';
import { Columns2, Columns3 } from 'lucide-react';

const NestedEditor = lazy(() => import('./NestedEditor'));

// ── Helpers ───────────────────────────────────────────────────────────────

function parseColumns(json: string, count: number): string[] {
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) {
      // Ensure we have enough columns
      while (parsed.length < count) parsed.push('');
      return parsed.slice(0, count);
    }
  } catch { /* empty */ }
  return Array.from({ length: count }, () => '');
}

// ── Renderer ──────────────────────────────────────────────────────────────

interface ColumnBlockRendererProps {
  block: {
    id: string;
    props: { columnCount: string; columns: string };
  };
  editor: {
    updateBlock: (id: string, update: { props: Partial<{ columnCount: string; columns: string }> }) => void;
    isEditable: boolean;
  };
}

function ColumnBlockRenderer({ block, editor }: ColumnBlockRendererProps) {
  const count = parseInt(block.props.columnCount, 10) || 2;
  const isEditable = editor.isEditable;

  const columnData = useMemo(
    () => parseColumns(block.props.columns, count),
    [block.props.columns, count]
  );

  const handleColumnChange = useCallback(
    (index: number, content: string) => {
      const updated = [...columnData];
      updated[index] = content;
      editor.updateBlock(block.id, {
        props: { columns: JSON.stringify(updated) },
      });
    },
    [block.id, editor, columnData]
  );

  const toggleColumnCount = useCallback(() => {
    const newCount = count === 2 ? 3 : 2;
    const updated = parseColumns(block.props.columns, newCount);
    editor.updateBlock(block.id, {
      props: {
        columnCount: String(newCount),
        columns: JSON.stringify(updated),
      },
    });
  }, [block.id, block.props.columns, count, editor]);

  return (
    <div className="column-block my-2" contentEditable={false}>
      {/* Toolbar */}
      {isEditable && (
        <div className="flex items-center gap-1 mb-1">
          <button
            onClick={toggleColumnCount}
            className="flex items-center gap-1 px-2 py-0.5 text-xs text-stone-500 hover:text-stone-700
                       hover:bg-stone-100 rounded transition-colors"
            title={count === 2 ? 'Switch to 3 columns' : 'Switch to 2 columns'}
          >
            {count === 2 ? (
              <>
                <Columns3 className="w-3.5 h-3.5" /> 3 cols
              </>
            ) : (
              <>
                <Columns2 className="w-3.5 h-3.5" /> 2 cols
              </>
            )}
          </button>
        </div>
      )}

      {/* Column grid */}
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(${count}, 1fr)` }}
      >
        {columnData.map((colContent, idx) => (
          <div
            key={idx}
            className="min-h-[60px] rounded-lg border border-stone-200 bg-white p-2"
          >
            <Suspense
              fallback={
                <div className="text-xs text-stone-400 py-2">Loading...</div>
              }
            >
              <NestedEditor
                initialContent={colContent}
                onChange={(content) => handleColumnChange(idx, content)}
                editable={isEditable}
                placeholder={`Column ${idx + 1}...`}
                debounceMs={400}
              />
            </Suspense>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Block spec ────────────────────────────────────────────────────────────

export const columnBlockSpec = createReactBlockSpec(
  {
    type: 'columns' as const,
    propSchema: {
      columnCount: { default: '2' },
      columns: { default: '["",""]' },
    },
    content: 'none',
  },
  {
    render: (props) => {
      const blockProps: ColumnBlockRendererProps = {
        block: {
          id: props.block.id,
          props: props.block.props as { columnCount: string; columns: string },
        },
        editor: {
          updateBlock: (id, update) => (props.editor as any).updateBlock(id, update),
          isEditable: (props.editor as any).isEditable,
        },
      };
      return <ColumnBlockRenderer {...blockProps} />;
    },
  }
);

export default columnBlockSpec;
